import React, { useState, useRef, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../../components/common';
import { FiX, FiUploadCloud, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { BrokerType, uploadPortfolioCSV } from '../../api/portfolio';
import strings from '../../utils/strings';

interface PortfolioUploadModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BROKERS: { id: BrokerType; name: string; description: string }[] = [
  { id: 'zerodha', name: 'Zerodha', description: 'Kite / Console holdings export' },
  { id: 'icici_direct', name: 'ICICI Direct', description: 'Portfolio holdings download' },
  { id: 'motilal_oswal', name: 'Motilal Oswal', description: 'Demat holdings export' },
];

const PortfolioUploadModal: React.FC<PortfolioUploadModalProps> = ({ isVisible, onClose, onSuccess }) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedBroker, setSelectedBroker] = useState<BrokerType | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const resetState = useCallback(() => {
    setSelectedBroker(null);
    setSelectedFile(null);
    setUploading(false);
    setError(null);
    setSuccessMsg(null);
    setDragOver(false);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !['csv', 'xlsx', 'xls'].includes(ext)) {
      setError('Please select a .csv or .xlsx file');
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = async () => {
    if (!selectedBroker || !selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      const result = await uploadPortfolioCSV(selectedFile, selectedBroker);
      setSuccessMsg(result.message);
      setTimeout(() => {
        handleClose();
        onSuccess();
      }, 1200);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.error || (typeof data?.detail === 'string' ? data.detail : null) || 'Upload failed. Please check the file format and try again.';
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[100] p-4 animate-fade-in"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={handleClose}
    >
      <div
        className="rounded-2xl w-full max-w-lg overflow-hidden animate-scale-in"
        style={{
          backgroundColor: colors.cardBackground || colors.backgroundColor,
          boxShadow: colors.shadowLarge,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-5 flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
          }}
        >
          <CText type="B18" color="#fff">
            {strings.uploadFile}
          </CText>
          <button
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            {React.createElement(FiX as any, { size: 20, color: '#fff' })}
          </button>
        </div>

        <div className="p-5">
          {/* Success state */}
          {successMsg ? (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: colors.green + '20' }}
              >
                {React.createElement(FiCheck as any, { size: 32, color: colors.green })}
              </div>
              <CText type="B16" color={colors.textColor}>
                {successMsg}
              </CText>
            </div>
          ) : (
            <>
              {/* Step 1: Broker Selection */}
              <CText type="M14" color={colors.textColor} style={{ marginBottom: 12 }}>
                {strings.selectBroker}
              </CText>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {BROKERS.map((broker) => (
                  <button
                    key={broker.id}
                    onClick={() => { setSelectedBroker(broker.id); setError(null); }}
                    className="p-3 rounded-xl border-2 text-left transition-all duration-150"
                    style={{
                      borderColor: selectedBroker === broker.id ? colors.primary : colors.borderColor,
                      backgroundColor: selectedBroker === broker.id ? colors.primary + '10' : 'transparent',
                    }}
                  >
                    <CText
                      type="M14"
                      color={selectedBroker === broker.id ? colors.primary : colors.textColor}
                      style={{ fontWeight: 600 }}
                    >
                      {broker.name}
                    </CText>
                    <CText type="r12" color={colors.grayScale500} style={{ marginTop: 2 }}>
                      {broker.description}
                    </CText>
                  </button>
                ))}
              </div>

              {/* Step 2: File Upload */}
              <div
                className={`rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-all duration-150 ${dragOver ? 'scale-[1.02]' : ''}`}
                style={{
                  borderColor: dragOver ? colors.primary : colors.borderColor,
                  backgroundColor: dragOver ? colors.primary + '08' : 'transparent',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                />
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: colors.primary + '15' }}
                >
                  {React.createElement(FiUploadCloud as any, { size: 24, color: colors.primary })}
                </div>
                {selectedFile ? (
                  <>
                    <CText type="M14" color={colors.textColor}>
                      {selectedFile.name}
                    </CText>
                    <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB - Click to change
                    </CText>
                  </>
                ) : (
                  <>
                    <CText type="M14" color={colors.textColor}>
                      {strings.dragDropFile}
                    </CText>
                    <CText type="r12" color={colors.grayScale500} style={{ marginTop: 4 }}>
                      {strings.supportedFormats}
                    </CText>
                  </>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 mt-3">
                  {React.createElement(FiAlertCircle as any, { size: 16, color: colors.alertColor })}
                  <CText type="r12" color={colors.alertColor}>
                    {error}
                  </CText>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={handleUpload}
                disabled={!selectedBroker || !selectedFile || uploading}
                className="w-full py-3.5 rounded-xl font-semibold text-base mt-5 transition-all duration-200"
                style={{
                  background: selectedBroker && selectedFile && !uploading
                    ? `linear-gradient(135deg, ${colors.primary}, #9F03F2)`
                    : colors.borderColor,
                  color: selectedBroker && selectedFile && !uploading ? '#fff' : colors.grayScale500,
                  cursor: selectedBroker && selectedFile && !uploading ? 'pointer' : 'not-allowed',
                }}
              >
                {uploading ? strings.uploading : strings.uploadFile}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioUploadModal;
